// DeviceListPage – main devices grid with filters, live status and bulk commands.

import React, { JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';

import CommandSubmitForm from '../../CommandSubmitForm/CommandSubmitForm';
import DeviceConfigureForm from '../../DeviceConfigureForm/DeviceConfigureForm';
import SelectRequestDialog from '../dialogs/SelectRequestView';
import { AddDeviceDialog, type AddDeviceMode } from '../dialogs/AddDeviceDialog';

import {
  DeviceMasterRow,
  ExtraColumnKey,
  DeviceStatus,
  DEFAULT_DEVICE_FILTERS,
  extraColumns,
} from '../../../../types/DeviceListTypes';

import type { CommandDdbItem, CommandOperation } from '../../../../types/Commands';

import { DeviceListRequestChip } from './DeviceListRequestChip';
import { DeviceListToolbar } from './DeviceListToolbar';
import { DeviceTable } from './DeviceListTable';
import { OfflineConfirmDialog } from '../dialogs/OfflineConfirmDialog';
import { DeviceDetailsDialog } from '../dialogs/DeviceDetailsDialog';
import { getActiveFilterChips } from '../utils/activeFilterChips';
import { PAGE_TAB_HEADER, SCOPE_PANEL_SURFACE } from '../../shared/ui';
import TableLoadingOverlay from '../../shared/TableLoadingOverlay';
import type { RaceValidationReportRow } from '../../shared/types/ValidationReportTypes';
import ValidationReportDialog from '../../shared/ValidationReportDialog';

import {
  STORAGE_SEARCH_KEY,
  STORAGE_MASTER_COLS_KEY,
  STORAGE_EXTRA_COLS_KEY,
  ONLINE_TIMEOUT_MS,
  REQUEST_FILTER_CHIP,
  SELECT,
  BTN_SECONDARY,
} from '../utils/deviceListConstants';
import {
  loadSearchQuery,
  loadVisibleMasterColumns,
  loadVisibleExtraColumns,
  loadShowAdjacentPairs,
  saveShowAdjacentPairs,
} from '../utils/deviceListStorage';
import { isCommandActive, isDeviceBusy, isFleetDeviceOnline } from '../utils/deviceListHelpers';
import { useDeviceListData } from '../utils/useDeviceListData';
import { useDeviceListLiveData } from '../utils/useDeviceListLiveData';
import { useRequestFilter } from '../utils/useRequestFilter';
import { useDeviceListSorting } from '../utils/useDeviceListSorting';
import { useDeviceListFiltering } from '../utils/useDeviceListFiltering';
import {
  buildGlobalUserIdToDisplayName,
  formatApiResponseError,
  type RaceConfigurationRequirements,
} from '../../EventsManagementView/utils';
import { getDeviceValidationBreakdown, getDeviceValidationMismatchReasons } from '../../../helpers/deviceValidation';
import {
  isRearRaceModeActive,
  pairedFrontIdFromRear,
  pairedRearIdFromFront,
} from '../../../helpers/devicePairing';
import { withCache, bustCache } from '../../../helpers/apiCache';
import {
  buildDeviceMappingColumns,
  buildDeviceMappingIdSet,
  normalizeDeviceMappingRow,
  type DeviceMappingRow,
} from '../../../helpers/deviceMappingRows';
import { parseDeviceMappingsCsv } from '../../../helpers/deviceMappingsCsv';

// ---- Component ----

// In-memory cache TTLs so navigating away/back doesn't re-fire these mount
// requests every time. The module-level cache survives unmounts.
const USERS_CACHE_TTL = 5 * 60_000;
const EVENTS_CACHE_TTL = 60_000;
const LIFECYCLE_CACHE_TTL = 2 * 60_000;
const ATTRIBUTES_CACHE_TTL = 5 * 60_000;

function summarizeDeviceIdsForToast(label: string, ids: string[], maxPreview = 4): string {
  const trimmed = ids.map((id) => String(id ?? '').trim()).filter(Boolean);
  if (trimmed.length === 0) return `${label} (0)`;
  const preview = trimmed.slice(0, maxPreview).join(', ');
  const remaining = trimmed.length - Math.min(trimmed.length, maxPreview);
  return remaining > 0
    ? `${label} (${trimmed.length}): ${preview} +${remaining} more`
    : `${label} (${trimmed.length}): ${preview}`;
}

function groupPairedRows(rows: DeviceMasterRow[], idKey: string): DeviceMasterRow[] {
  type Group = {
    front?: DeviceMasterRow;
    rear?: DeviceMasterRow;
    others: DeviceMasterRow[];
  };

  const orderedGroupKeys: string[] = [];
  const groups = new Map<string, Group>();

  const ensureGroup = (groupKey: string): Group => {
    if (!groups.has(groupKey)) {
      groups.set(groupKey, { others: [] });
      orderedGroupKeys.push(groupKey);
    }
    return groups.get(groupKey)!;
  };

  for (const row of rows) {
    const rawId = String(row[idKey] ?? '').trim();
    if (!rawId) {
      const key = `__blank_${orderedGroupKeys.length}`;
      ensureGroup(key).others.push(row);
      continue;
    }

    const lower = rawId.toLowerCase();
    const isRear = lower.startsWith('rear_');
    const isFront = lower.startsWith('front_');

    const groupKey = isRear
      ? `pair_${rawId.slice(5)}`
      : isFront
        ? `pair_${rawId.slice(6)}`
        : `single_${rawId}`;

    const group = ensureGroup(groupKey);
    if (isRear) group.rear = row;
    else if (isFront) group.front = row;
    else group.others.push(row);
  }

  const out: DeviceMasterRow[] = [];
  for (const key of orderedGroupKeys) {
    const group = groups.get(key);
    if (!group) continue;
    if (group.rear) out.push(group.rear);
    if (group.front) out.push(group.front);
    if (group.others.length) out.push(...group.others);
  }
  return out;
}

import { filterUnretrievedEventDeviceIds } from '../../EventsManagementView/utils/raceRetrievalCounts';
import type { DevicePageProps } from '../types/DeviceListViewProps';

export type { DevicePageProps };

export default function DevicePage(props?: DevicePageProps): JSX.Element {
  const {
    onCenterMapOnLocation,
    operatorScopedEventId = null,
    operatorScopedEventName = null,
    operatorScopedRaceId = null,
    operatorScopedUnretrievedOnly = false,
    mapRadiusFilter = null,
    mapSelectedDeviceIds = null,
    onClearMapRadiusFilter,
    onVisibleIdsBeforeLocationFilterChange,
  } = props ?? {};
  const { isAdmin, isOperator } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const pendingAdminDevicesRaceFromUrlRef = useRef<string | null>(null);
  const pendingAdminDevicesUnretrievedOnlyRef = useRef(false);
  const operatorSelectedEventLabel = useMemo(() => {
    const eventId = String(operatorScopedEventId ?? '').trim();
    const eventName = String(operatorScopedEventName ?? '').trim();
    if (!eventId) return '';
    return eventName ? `${eventName} (${eventId})` : eventId;
  }, [operatorScopedEventId, operatorScopedEventName]);
  const [operatorScopedDeviceIds, setOperatorScopedDeviceIds] = useState<string[] | null>(null);
  const [operatorScopeLoading, setOperatorScopeLoading] = useState(false);
  const [operatorScopeError, setOperatorScopeError] = useState<string | null>(null);
  const [dataRefreshKey, setDataRefreshKey] = useState(0);
  const [hardRefreshing, setHardRefreshing] = useState(false);
  const hardRefreshPendingRef = useRef(false);
  const hardRefreshSawLoadingRef = useRef(false);
  const tlsDeviceIds =
    isOperator && !isAdmin ? (operatorScopedDeviceIds ?? []) : undefined;
  const { rows, columns, loading, error, tlsMap, tlsLoading } = useDeviceListData({
    enableTlsCheck: isAdmin,
    tlsDeviceIds,
    reloadKey: dataRefreshKey,
  });
  const liveSeedDeviceIds = useMemo(() => {
    if (isOperator && !isAdmin) {
      return (operatorScopedDeviceIds ?? [])
        .map((deviceId) => String(deviceId ?? '').trim())
        .filter((deviceId) => deviceId.length > 0);
    }

    const idColumn = columns[0];
    if (!idColumn) return [];

    return rows
      .map((row) => row[idColumn])
      .filter((deviceId): deviceId is string => typeof deviceId === 'string' && deviceId.trim().length > 0)
      .map((deviceId) => deviceId.trim());
  }, [columns, isAdmin, isOperator, operatorScopedDeviceIds, rows]);

  const lifecycleFetchKey = useMemo(() => liveSeedDeviceIds.join('\u0001'), [liveSeedDeviceIds]);

  const [lifecycleMap, setLifecycleMap] = useState<Record<string, Record<string, unknown>>>({});
  const [lifecycleLoading, setLifecycleLoading] = useState(false);
  const [lifecycleLoadedOnce, setLifecycleLoadedOnce] = useState(false);
  const [lifecycleRefreshKey, setLifecycleRefreshKey] = useState(0);

  const [attributesMap, setAttributesMap] = useState<Record<string, Record<string, unknown>>>({});
  const [attributesLoading, setAttributesLoading] = useState(false);

  const {
    activeRequestId,
    requestFilterIds,
    requestLatestCommandMap,
    setRequestLatestCommandMap,
    activeRequestIdRef,
    setUrlRequestId,
    clearActiveRequest,
    clearActiveRequestStateOnly,
    activateRequest,
  } = useRequestFilter({
    operatorScopedEventId: isOperator && !isAdmin ? operatorScopedEventId : null,
  });

  const {
    statusMap,
    lastStatusSeen,
    now,
    latestCommandMap,
    commandStatusMap,
    liveDataLoading,
    statusLoading,
    commandLoading,
  } = useDeviceListLiveData({
    activeRequestIdRef,
    setRequestLatestCommandMap,
    deviceIds: liveSeedDeviceIds,
    operatorScopedEventId: isOperator && !isAdmin ? operatorScopedEventId : null,
    statusReloadKey: dataRefreshKey,
  });

  /** When a request is active, use its command map; otherwise use global latest. */
  const displayedLatestCommandMap = useMemo(() => {
    return activeRequestId ? requestLatestCommandMap : latestCommandMap;
  }, [activeRequestId, requestLatestCommandMap, latestCommandMap]);

  useEffect(() => {
    if (!isAdmin || !window.eventsAPI?.getDeviceLifecycle) {
      setLifecycleMap({});
      setLifecycleLoadedOnce(false);
      setLifecycleLoading(false);
      return;
    }

    if (liveSeedDeviceIds.length === 0) {
      setLifecycleMap({});
      setLifecycleLoadedOnce(true);
      setLifecycleLoading(false);
      return;
    }

    let cancelled = false;
    setLifecycleLoading(true);
    setLifecycleLoadedOnce(false);

    (async () => {
      try {
        const merged = await withCache(
          `lifecycle:${lifecycleFetchKey}`,
          LIFECYCLE_CACHE_TTL,
          async () => {
            const acc: Record<string, Record<string, unknown>> = {};
            const chunk = 100;
            const slices: string[][] = [];
            for (let i = 0; i < liveSeedDeviceIds.length; i += chunk) {
              slices.push(liveSeedDeviceIds.slice(i, i + chunk));
            }
            const results = await Promise.all(
              slices.map((slice) => window.eventsAPI.getDeviceLifecycle({ deviceIds: slice })),
            );
            let hadError = false;
            for (const res of results) {
              if (!res.ok) {
                if (!hadError) {
                  hadError = true;
                  window.dispatchEvent(
                    new CustomEvent('toast-error', {
                      detail: String(res.error ?? 'Failed to load lifecycle status'),
                    }),
                  );
                }
                continue;
              }
              const rows = res.data?.devices;
              if (Array.isArray(rows)) {
                for (const r of rows) {
                  const id = String((r as { deviceId?: unknown }).deviceId ?? '').trim();
                  if (id) acc[id] = r as Record<string, unknown>;
                }
              }
            }
            return acc;
          },
        );
        if (!cancelled) setLifecycleMap(merged);
      } finally {
        if (!cancelled) {
          setLifecycleLoading(false);
          setLifecycleLoadedOnce(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAdmin, lifecycleFetchKey, lifecycleRefreshKey, dataRefreshKey]);

  useEffect(() => {
    if (!window.eventsAPI?.getDeviceAttributes || liveSeedDeviceIds.length === 0) {
      setAttributesMap({});
      setAttributesLoading(false);
      return;
    }

    let cancelled = false;
    setAttributesLoading(true);

    (async () => {
      try {
        const merged = await withCache(
          `attrs:${lifecycleFetchKey}`,
          ATTRIBUTES_CACHE_TTL,
          async () => {
            const acc: Record<string, Record<string, unknown>> = {};
            const chunk = 100;
            const slices: string[][] = [];
            for (let i = 0; i < liveSeedDeviceIds.length; i += chunk) {
              slices.push(liveSeedDeviceIds.slice(i, i + chunk));
            }
            const results = await Promise.all(
              slices.map((slice) => window.eventsAPI.getDeviceAttributes({ deviceIds: slice })),
            );
            for (const res of results) {
              if (!res.ok) continue;
              const rows = res.data?.devices;
              if (Array.isArray(rows)) {
                for (const r of rows) {
                  const id = String((r as { deviceId?: unknown }).deviceId ?? '').trim();
                  if (id) acc[id] = r as Record<string, unknown>;
                }
              }
            }
            return acc;
          },
        );
        if (!cancelled) setAttributesMap(merged);
      } finally {
        if (!cancelled) setAttributesLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [lifecycleFetchKey, dataRefreshKey]);

  const [userIdToDisplayName, setUserIdToDisplayName] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const api = window.eventsAPI as { listUsers?: () => Promise<{ ok?: boolean; data?: unknown }> } | undefined;
        if (!api?.listUsers) return;
        const res = await withCache('users', USERS_CACHE_TTL, () => api.listUsers!());
        if (cancelled || !res?.ok) return;
        const ud = res.data as { users?: unknown[] } | unknown[] | undefined;
        const users = Array.isArray(ud) ? ud : Array.isArray(ud?.users) ? ud.users : [];
        setUserIdToDisplayName(buildGlobalUserIdToDisplayName(users));
      } catch {
        if (!cancelled) setUserIdToDisplayName({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dataRefreshKey]);

  const [offlineConfirmBlockedIds, setOfflineConfirmBlockedIds] = useState<string[]>([]);

  const [showAdjacentPairs, setShowAdjacentPairs] = useState(() => loadShowAdjacentPairs());

  // Modals: command form, configure (offline ops), offline confirm; device list for the form.
  const [showCommandForm, setShowCommandForm] = useState(false);
  const [showConfigureForm, setShowConfigureForm] = useState(false);
  const [commandDeviceIds, setCommandDeviceIds] = useState<string[]>([]);

  const [showOfflineConfirm, setShowOfflineConfirm] = useState(false);
  const [offlineConfirmOnlineIds, setOfflineConfirmOnlineIds] = useState<string[]>([]);
  const [offlineConfirmOfflineIds, setOfflineConfirmOfflineIds] = useState<string[]>([]);

  // Add device mapping (master table) modal — single row (one field per live column) or bulk CSV.
  const [showAddDeviceDialog, setShowAddDeviceDialog] = useState(false);
  const [addDeviceMode, setAddDeviceMode] = useState<AddDeviceMode>('single');
  const [addDeviceColumns, setAddDeviceColumns] = useState<string[]>(['name']);
  const [addDeviceColumnsLoading, setAddDeviceColumnsLoading] = useState(false);
  const [addDeviceValues, setAddDeviceValues] = useState<Record<string, string>>({ name: '' });
  const [addDeviceSaving, setAddDeviceSaving] = useState(false);
  const [bulkCsvFileName, setBulkCsvFileName] = useState('');
  const [bulkCsvRows, setBulkCsvRows] = useState<DeviceMappingRow[]>([]);
  const [bulkCsvError, setBulkCsvError] = useState('');
  const [bulkUploading, setBulkUploading] = useState(false);

  // Column visibility (persisted to localStorage).
  const [visibleMasterColumns, setVisibleMasterColumns] = useState<string[]>(() =>
    loadVisibleMasterColumns(),
  );
  const [visibleExtraColumns, setVisibleExtraColumns] = useState<ExtraColumnKey[]>(() =>
    loadVisibleExtraColumns(),
  );

  const [columnsMenuOpen, setColumnsMenuOpen] = useState(false);

  // Selection and device details dialog.
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [deviceDetailsDialogId, setDeviceDetailsDialogId] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState<string>(() => loadSearchQuery());
  const [csvFilterIds, setCsvFilterIds] = useState<string[] | null>(null);
  const [eventFilterId, setEventFilterId] = useState<string>('');
  const [raceFilterId, setRaceFilterId] = useState<string>('');
  const [eventFilterDeviceIds, setEventFilterDeviceIds] = useState<string[] | null>(null);
  const [eventFilterEvents, setEventFilterEvents] = useState<
    Array<{ eventId: string; name?: string; status?: string }>
  >([]);
  const [eventFilterRaces, setEventFilterRaces] = useState<
    Array<{ raceId: string; name?: string; status?: string }>
  >([]);
  const [eventFilterEventsLoading, setEventFilterEventsLoading] = useState(false);
  const [eventFilterRacesLoading, setEventFilterRacesLoading] = useState(false);
  const [eventFilterDevicesLoading, setEventFilterDevicesLoading] = useState(false);
  const [eventFilterError, setEventFilterError] = useState<string | null>(null);
  const [eventFilterUnretrievedOnly, setEventFilterUnretrievedOnly] = useState(false);
  const [validationModeActive, setValidationModeActive] = useState(false);
  const [validationModeBusy, setValidationModeBusy] = useState(false);
  const [validationEventId, setValidationEventId] = useState('');
  const [validationRaceId, setValidationRaceId] = useState('');
  const [validationSelectedDeviceIds, setValidationSelectedDeviceIds] = useState<string[]>([]);
  const [validationFailureMap, setValidationFailureMap] = useState<Record<string, string[]>>({});
  const [validationRaceCfg, setValidationRaceCfg] = useState<RaceConfigurationRequirements | null>(null);
  const [validationStatusById, setValidationStatusById] = useState<Record<string, DeviceStatus>>({});
  const [showValidationReport, setShowValidationReport] = useState(false);

  const [selectRequestOpen, setSelectRequestOpen] = useState(false);

  const idKey = columns[0];

  useEffect(() => {
    if (!isOperator || isAdmin) {
      setOperatorScopedDeviceIds(null);
      setOperatorScopeLoading(false);
      setOperatorScopeError(null);
      return;
    }

    const eventId = String(operatorScopedEventId ?? '').trim();
    if (!eventId) {
      setOperatorScopedDeviceIds([]);
      setOperatorScopeLoading(false);
      setOperatorScopeError(null);
      return;
    }

    let cancelled = false;

    async function loadOperatorEventDevices(): Promise<void> {
      if (!window.eventsAPI?.listEventDevices) {
        setOperatorScopedDeviceIds([]);
        setOperatorScopeError('Events API is not available');
        return;
      }

      setOperatorScopeLoading(true);
      setOperatorScopeError(null);

      try {
        const raceOpt = String(operatorScopedRaceId ?? '').trim();
        const res = await window.eventsAPI.listEventDevices(eventId, {
          limit: 5000,
          ...(raceOpt ? { raceId: raceOpt } : {}),
        });
        if (cancelled) return;

        if (!res.ok) {
          setOperatorScopedDeviceIds([]);
          setOperatorScopeError(`Failed to load event devices (${res.status ?? 'error'})`);
          return;
        }

        const data = res.data as {
          devices?: Array<{ deviceId?: unknown; retrieved?: number; recalled?: number }>;
        } | undefined;
        const rawDevices = Array.isArray(data?.devices) ? data.devices : [];
        const deviceIds = operatorScopedUnretrievedOnly
          ? filterUnretrievedEventDeviceIds(rawDevices)
          : rawDevices
            .map((row) => String(row?.deviceId ?? '').trim())
            .filter((deviceId) => deviceId.length > 0);

        setOperatorScopedDeviceIds(Array.from(new Set(deviceIds)));
      } catch (err: any) {
        if (cancelled) return;
        setOperatorScopedDeviceIds([]);
        setOperatorScopeError(err?.message || 'Failed to load event devices');
      } finally {
        if (!cancelled) setOperatorScopeLoading(false);
      }
    }

    void loadOperatorEventDevices();
    return () => {
      cancelled = true;
    };
  }, [isAdmin, isOperator, operatorScopedEventId, operatorScopedRaceId, operatorScopedUnretrievedOnly, dataRefreshKey]);

  /** When request filter is active, drop any selected IDs that are not in the request. */
  useEffect(() => {
    if (!activeRequestId || !requestFilterIds) return;
    const allowed = new Set(requestFilterIds);
    setSelectedIds((prev) => prev.filter((id) => allowed.has(id)));
  }, [activeRequestId, requestFilterIds]);

  useEffect(() => {
    if (!isAdmin) {
      setEventFilterId('');
      setRaceFilterId('');
      setEventFilterDeviceIds(null);
      setEventFilterEvents([]);
      setEventFilterRaces([]);
      setEventFilterEventsLoading(false);
      setEventFilterRacesLoading(false);
      setEventFilterDevicesLoading(false);
      setEventFilterError(null);
      return;
    }
    if (!window.eventsAPI?.listEvents) {
      setEventFilterEvents([]);
      setEventFilterError('Events API is not available');
      return;
    }

    let cancelled = false;
    setEventFilterEventsLoading(true);
    setEventFilterError(null);

    async function loadEventFilterOptions(): Promise<void> {
      try {
        const res = await withCache('events:200', EVENTS_CACHE_TTL, () =>
          window.eventsAPI.listEvents({ limit: 200 }),
        );
        if (cancelled) return;
        if (!res.ok) {
          setEventFilterEvents([]);
          setEventFilterError(`Failed to load events (${res.status ?? 'error'})`);
          return;
        }

        const data = res.data as {
          events?: Array<{ eventId?: unknown; name?: unknown; status?: unknown }>;
        } | undefined;
        const rows = Array.isArray(data?.events) ? data.events : [];
        setEventFilterEvents(
          rows
            .map((row) => ({
              eventId: String(row?.eventId ?? '').trim(),
              name: typeof row?.name === 'string' ? row.name : undefined,
              status: typeof row?.status === 'string' ? row.status : undefined,
            }))
            .filter((row) => row.eventId.length > 0),
        );
      } catch (err: any) {
        if (cancelled) return;
        setEventFilterEvents([]);
        setEventFilterError(err?.message || 'Failed to load events');
      } finally {
        if (!cancelled) setEventFilterEventsLoading(false);
      }
    }

    void loadEventFilterOptions();
    return () => {
      cancelled = true;
    };
  }, [isAdmin, dataRefreshKey]);

  useEffect(() => {
    if (!isAdmin || !eventFilterId) {
      setEventFilterRaces([]);
      setEventFilterRacesLoading(false);
      return;
    }
    if (!window.eventsAPI?.listEventRaces) {
      setEventFilterRaces([]);
      return;
    }

    let cancelled = false;
    setEventFilterRaces([]);
    setEventFilterRacesLoading(true);

    async function loadRacesForEvent(): Promise<void> {
      try {
        const res = await window.eventsAPI.listEventRaces(eventFilterId);
        if (cancelled) return;
        if (!res.ok) {
          pendingAdminDevicesRaceFromUrlRef.current = null;
          setEventFilterRaces([]);
          return;
        }
        const data = res.data as {
          races?: Array<{ raceId?: unknown; name?: unknown; status?: unknown }>;
        } | undefined;
        const rows = Array.isArray(data?.races) ? data!.races! : [];
        const mapped = rows
          .map((row) => ({
            raceId: String(row?.raceId ?? '').trim(),
            name: typeof row?.name === 'string' ? row.name : undefined,
            status: typeof row?.status === 'string' ? row.status : undefined,
          }))
          .filter((row) => row.raceId.length > 0);
        setEventFilterRaces(mapped);

        // Apply `/?raceId=` from Events → Devices here: a separate effect can run before loading flips
        // and clear `pendingAdminDevicesRaceFromUrlRef` while `eventFilterRaces` is still empty/stale.
        const wantRace = pendingAdminDevicesRaceFromUrlRef.current;
        if (!cancelled && wantRace) {
          if (mapped.some((r) => r.raceId === wantRace)) {
            setRaceFilterId(wantRace);
          }
          pendingAdminDevicesRaceFromUrlRef.current = null;
        }
      } catch {
        if (!cancelled) {
          pendingAdminDevicesRaceFromUrlRef.current = null;
          setEventFilterRaces([]);
        }
      } finally {
        if (!cancelled) setEventFilterRacesLoading(false);
      }
    }

    void loadRacesForEvent();
    return () => {
      cancelled = true;
    };
  }, [eventFilterId, isAdmin, dataRefreshKey]);

  useEffect(() => {
    if (!isAdmin) return;
    if (!eventFilterId) {
      setRaceFilterId('');
      setEventFilterDeviceIds(null);
      setEventFilterDevicesLoading(false);
      setEventFilterError(null);
      return;
    }
    if (!window.eventsAPI?.listEventDevices) {
      setEventFilterDeviceIds([]);
      setEventFilterError('Events API is not available');
      return;
    }

    let cancelled = false;
    setEventFilterDevicesLoading(true);
    setEventFilterError(null);

    async function loadEventFilteredDevices(): Promise<void> {
      try {
        const res = await window.eventsAPI.listEventDevices(eventFilterId, {
          limit: 5000,
          ...(raceFilterId.trim() ? { raceId: raceFilterId.trim() } : {}),
        });
        if (cancelled) return;
        if (!res.ok) {
          setEventFilterDeviceIds([]);
          setEventFilterError(
            raceFilterId.trim()
              ? `Failed to load devices for race ${raceFilterId.trim()} on event ${eventFilterId}`
              : `Failed to load devices for event ${eventFilterId}`,
          );
          return;
        }
        const data = res.data as {
          devices?: Array<{ deviceId?: unknown; retrieved?: number; recalled?: number }>;
        } | undefined;
        const rawDevices = Array.isArray(data?.devices) ? data.devices : [];
        const deviceIds = eventFilterUnretrievedOnly
          ? filterUnretrievedEventDeviceIds(rawDevices)
          : rawDevices
            .map((row) => String(row?.deviceId ?? '').trim())
            .filter((id) => id.length > 0);
        setEventFilterDeviceIds(Array.from(new Set(deviceIds)));
      } catch (err: any) {
        if (cancelled) return;
        setEventFilterDeviceIds([]);
        setEventFilterError(
          err?.message ||
            (raceFilterId.trim()
              ? `Failed to load devices for race ${raceFilterId.trim()} on event ${eventFilterId}`
              : `Failed to load devices for event ${eventFilterId}`),
        );
      } finally {
        if (!cancelled) setEventFilterDevicesLoading(false);
      }
    }

    void loadEventFilteredDevices();
    return () => {
      cancelled = true;
    };
  }, [eventFilterId, raceFilterId, eventFilterUnretrievedOnly, isAdmin, dataRefreshKey]);

  useEffect(() => {
    if (!raceFilterId.trim()) return;
    if (eventFilterRacesLoading) return;
    if (eventFilterRaces.length === 0) return;
    if (!eventFilterRaces.some((r) => r.raceId === raceFilterId)) {
      setRaceFilterId('');
    }
  }, [raceFilterId, eventFilterRaces, eventFilterRacesLoading]);

  /** Admin: `/?eventId=…&raceId=…&unretrievedOnly=1` from Events → Devices (other query keys preserved). */
  useEffect(() => {
    if (!isAdmin) return;
    const raw = location.search ?? '';
    if (!raw || raw === '?') return;
    const q = raw.startsWith('?') ? raw.slice(1) : raw;
    const sp = new URLSearchParams(q);
    const eventIdFromUrl = sp.get('eventId')?.trim() ?? '';
    if (!eventIdFromUrl) return;
    const raceFromUrl = sp.get('raceId')?.trim() ?? '';
    const unretrievedOnly = sp.get('unretrievedOnly') === '1' || sp.get('unretrievedOnly') === 'true';
    pendingAdminDevicesRaceFromUrlRef.current = raceFromUrl || null;
    pendingAdminDevicesUnretrievedOnlyRef.current = unretrievedOnly;
    setEventFilterId(eventIdFromUrl);
    setRaceFilterId('');
    setEventFilterUnretrievedOnly(unretrievedOnly);
    sp.delete('eventId');
    sp.delete('raceId');
    sp.delete('unretrievedOnly');
    const rest = sp.toString();
    const path = location.pathname && location.pathname !== '' ? location.pathname : '/';
    navigate({ pathname: path, search: rest ? `?${rest}` : '' }, { replace: true });
  }, [isAdmin, location.search, location.pathname, navigate]);

  /** When columns first arrive, set default visible master columns (preferred order) if none persisted. */
  useEffect(() => {
    if (!columns.length) return;

    setVisibleMasterColumns((prev) => {
      if (prev.length > 0) return prev;

      const [_idKey, ...rest] = columns;

      const preferredOrder = [
        'devicerole',
        'pairname',
        'macfront',
        'frontmac',
        'mac_front',
        'macrear',
        'rear_mac',
        'mac_rear',
        'description',
        'desc',
        'simid',
        'sim_id',
        'sim',
        'iccid',
      ];

      const lowerMap = new Map<string, string>();
      for (const c of rest) lowerMap.set(c.toLowerCase(), c);

      const defaults: string[] = [];
      for (const name of preferredOrder) {
        const match = lowerMap.get(name);
        if (match && !defaults.includes(match)) defaults.push(match);
      }

      return defaults.length ? defaults : rest;
    });
  }, [columns]);

  /** Persist visible master columns to localStorage. */
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(STORAGE_MASTER_COLS_KEY, JSON.stringify(visibleMasterColumns));
    } catch {
    }
  }, [visibleMasterColumns]);

  /** Persist visible extra columns to localStorage. */
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(STORAGE_EXTRA_COLS_KEY, JSON.stringify(visibleExtraColumns));
    } catch {
    }
  }, [visibleExtraColumns]);

  useEffect(() => { saveShowAdjacentPairs(showAdjacentPairs); }, [showAdjacentPairs]);

  const extraOrder = new Map<ExtraColumnKey, number>(extraColumns.map((c, i) => [c.key, i]));

  const scopedRows = rows;

  const effectiveLoading =
    loading ||
    operatorScopeLoading ||
    liveDataLoading ||
    (isAdmin && tlsLoading) ||
    (isAdmin && lifecycleLoading);

  const handleHardRefresh = useCallback(() => {
    bustCache();
    hardRefreshPendingRef.current = true;
    hardRefreshSawLoadingRef.current = false;
    setHardRefreshing(true);
    setDataRefreshKey((k) => k + 1);
    setLifecycleRefreshKey((k) => k + 1);
    void window.fleetAPI?.reconnectWebSocket?.();
  }, []);

  useEffect(() => {
    if (!hardRefreshPendingRef.current) return;

    if (effectiveLoading) {
      hardRefreshSawLoadingRef.current = true;
      return;
    }

    if (hardRefreshSawLoadingRef.current) {
      hardRefreshPendingRef.current = false;
      hardRefreshSawLoadingRef.current = false;
      setHardRefreshing(false);
      return;
    }

    // Scope may be empty so no loading flags flip — clear after effects have run.
    const t = window.setTimeout(() => {
      if (!hardRefreshPendingRef.current) return;
      hardRefreshPendingRef.current = false;
      setHardRefreshing(false);
    }, 100);
    return () => window.clearTimeout(t);
  }, [effectiveLoading, dataRefreshKey]);

  const deviceListLoadingPhrases = useMemo(() => {
    const phrases: string[] = [];
    if (loading) {
      phrases.push(
        isOperator && !isAdmin ? 'Loading event device list…' : 'Fetching device list…',
      );
    }
    if (operatorScopeLoading) {
      phrases.push('Loading assigned devices…');
    }
    if (isAdmin && lifecycleLoading) {
      phrases.push('Checking lifecycle status…');
    }
    if (statusLoading) {
      phrases.push('Getting device statuses…');
    }
    if (commandLoading) {
      phrases.push('Loading latest commands…');
    }
    if (isAdmin && tlsLoading) {
      phrases.push('Checking TLS certificates…');
    }
    if (phrases.length === 0) {
      phrases.push('Loading devices…');
    }
    return phrases;
  }, [
    loading,
    operatorScopeLoading,
    isAdmin,
    isOperator,
    lifecycleLoading,
    statusLoading,
    commandLoading,
    tlsLoading,
  ]);

  const effectiveError = error ?? operatorScopeError;

  function toggleExtraColumn(
    key: ExtraColumnKey,
    setVisibleExtraColumns2: React.Dispatch<React.SetStateAction<ExtraColumnKey[]>>,
  ) {
    setVisibleExtraColumns2((prev) => {
      if (prev.includes(key)) return prev.filter((k) => k !== key);

      const next = [...prev, key];
      next.sort((a, b) => (extraOrder.get(a) ?? 0) - (extraOrder.get(b) ?? 0));
      return next;
    });
  }

  function toggleMasterColumn(
    col: string,
    columns2: string[],
    setVisibleMasterColumns2: React.Dispatch<React.SetStateAction<string[]>>,
  ) {
    const masterColumnsExcludingId = columns2.slice(1);
    const masterOrder = new Map<string, number>(masterColumnsExcludingId.map((c, i) => [c, i]));

    setVisibleMasterColumns2((prev) => {
      if (prev.includes(col)) return prev.filter((c) => c !== col);

      const next = [...prev, col];
      next.sort((a, b) => (masterOrder.get(a) ?? 0) - (masterOrder.get(b) ?? 0));
      return next;
    });
  }

  /**
   * Busy: status.busy from backend, or active command before next device status post.
   *
   * Devices with no tracked command (e.g. a rear mid BLE-relay for a front operation)
   * rely purely on the raw telemetry busy flag, which can drop to 0 for a single
   * heartbeat mid-transfer and bounce back. Without holding busy across that gap, the
   * row's busy highlight/strobe animation unmounts and restarts every time the flag
   * blips, so it never visibly progresses. Latch busy true for BUSY_HOLD_MS after the
   * last time it was seen true to smooth over that flicker.
   */
  const lastBusyAtRef = useRef<Record<string, number>>({});
  const BUSY_HOLD_MS = 4000;
  const busyMap = useMemo(() => {
    const result: Record<string, boolean> = {};
    if (!idKey) return result;

    const nowTs = Date.now();
    for (const row of scopedRows) {
      const deviceId = row[idKey] as string | undefined;
      if (!deviceId) continue;
      const status = statusMap[deviceId];
      const latestCommand = displayedLatestCommandMap[deviceId];
      const cmdStatus =
        commandStatusMap[deviceId] ?? latestCommand?.status;
      const rawBusy = isDeviceBusy(
        status,
        cmdStatus,
        lastStatusSeen[deviceId],
        latestCommand?.updatedAt,
      );

      if (rawBusy) {
        lastBusyAtRef.current[deviceId] = nowTs;
        result[deviceId] = true;
      } else {
        const lastBusyAt = lastBusyAtRef.current[deviceId] ?? 0;
        result[deviceId] = nowTs - lastBusyAt < BUSY_HOLD_MS;
      }
    }

    // A command on either pair member runs over the rear's link and occupies both
    // devices. The backend marks both status rows busy at dispatch, but any fresh
    // heartbeat from either device wipes the flag on BOTH — even mid-command — so
    // whether a row keeps its busy flag is a race between submit time and heartbeat
    // cadence. The command record survives that wipe, so while the partner's command
    // is still active, hold this device busy too.
    for (const row of scopedRows) {
      const deviceId = row[idKey] as string | undefined;
      if (!deviceId || result[deviceId]) continue;
      const pairedId = pairedFrontIdFromRear(deviceId) ?? pairedRearIdFromFront(deviceId);
      if (!pairedId) continue;
      const pairedCmdStatus =
        commandStatusMap[pairedId] ?? displayedLatestCommandMap[pairedId]?.status;
      if (isCommandActive(pairedCmdStatus)) {
        lastBusyAtRef.current[deviceId] = nowTs;
        result[deviceId] = true;
      }
    }

    return result;
  }, [
    scopedRows,
    idKey,
    statusMap,
    lastStatusSeen,
    commandStatusMap,
    displayedLatestCommandMap,
    now,
  ]);

  /** Per-device: online if we got status recently or command is in progress. */
  const onlineMap = useMemo(() => {
    const cutoff = now - ONLINE_TIMEOUT_MS;
    const result: Record<string, boolean> = {};

    for (const row of scopedRows) {
      if (!idKey) continue;
      const deviceId = row[idKey] as string | undefined;
      if (!deviceId) continue;

      const cmdStatus = commandStatusMap[deviceId] ?? latestCommandMap[deviceId]?.status;
      result[deviceId] = isFleetDeviceOnline({
        deviceId,
        statusMap,
        lastStatusSeen,
        now,
        commandStatus: cmdStatus,
        onlineTimeoutMs: ONLINE_TIMEOUT_MS,
      });
    }

    // App mode only: mirror online across the pair. Never while rear is in race.
    // Only mirror to the front when the rear has explicitly proxied front_link_status
    // === 'CONNECTED'. A missing/empty/non-CONNECTED value means no live front block,
    // so the front must NOT inherit the rear's online state.
    for (const row of scopedRows) {
      const deviceId = String(row[idKey] ?? '').trim();
      if (!deviceId) continue;
      const pairedId = pairedFrontIdFromRear(deviceId) ?? pairedRearIdFromFront(deviceId);
      if (!pairedId) continue;

      const rearId = pairedRearIdFromFront(deviceId) ?? pairedFrontIdFromRear(deviceId) ?? deviceId;
      if (isRearRaceModeActive(statusMap, rearId)) continue;

      const frontId = pairedFrontIdFromRear(deviceId) ?? (pairedRearIdFromFront(deviceId) != null ? deviceId : null);
      if (frontId) {
        const frontStatus = statusMap[frontId] as Record<string, unknown> | undefined;
        const linkStatus = String(frontStatus?.front_link_status ?? '').trim().toUpperCase();
        if (linkStatus !== 'CONNECTED') continue;
      }

      const selfOnline = !!result[deviceId];
      const pairedOnline = !!result[pairedId];
      if (selfOnline || pairedOnline) {
        result[deviceId] = true;
        result[pairedId] = true;
      }
    }

    return result;
  }, [scopedRows, idKey, lastStatusSeen, now, commandStatusMap, latestCommandMap, statusMap]);

  const { requestSort, getSortIndicator, sortedRows } = useDeviceListSorting({
    rows: scopedRows,
    columns,
    idKey,
    statusMap,
    tlsMap,
    onlineMap,
    displayedLatestCommandMap,
    lifecycleMap: isAdmin ? lifecycleMap : {},
    attributesMap,
  });

  const { filters, setFilters, filteredRows, allVisibleIds, allVisibleIdsBeforeLocationFilter } = useDeviceListFiltering({
    sortedRows,
    searchQuery,
    columns,
    csvFilterIds,
    eventDeviceFilterIds: isAdmin ? eventFilterDeviceIds : null,
    activeRequestId,
    requestFilterIds,
    idKey,
    statusMap,
    tlsMap,
    displayedLatestCommandMap,
    onlineMap,
    busyMap,
    lifecycleFilterApplies: isAdmin && lifecycleLoadedOnce && !lifecycleLoading,
    lifecycleMap: isAdmin ? lifecycleMap : {},
    externalRadiusFilter: mapRadiusFilter,
    externalDeviceIdsFilter: mapSelectedDeviceIds,
  });

  useEffect(() => {
    onVisibleIdsBeforeLocationFilterChange?.(allVisibleIdsBeforeLocationFilter);
  }, [allVisibleIdsBeforeLocationFilter, onVisibleIdsBeforeLocationFilterChange]);

  const activeFilterCount = useMemo(() => {
    const chips = getActiveFilterChips(filters);
    const hasCsv = (csvFilterIds?.length ?? 0) > 0;
    return chips.length + (hasCsv ? 1 : 0);
  }, [filters, csvFilterIds]);

  const locationFilterActive = useMemo(() => {
    const hasPanelLocationFilter =
      filters.locationCenterLat != null
      && filters.locationCenterLng != null
      && filters.locationRadiusKm != null;
    return hasPanelLocationFilter || !!mapRadiusFilter;
  }, [filters.locationCenterLat, filters.locationCenterLng, filters.locationRadiusKm, mapRadiusFilter]);

  function handleClearLocationFilter(): void {
    setFilters((prev) => ({
      ...prev,
      locationCenterLat: null,
      locationCenterLng: null,
      locationRadiusKm: null,
    }));
    onClearMapRadiusFilter?.();
  }

  /** Reset all panel/CSV/location filters. Leaves the text search untouched
   *  (the search box has its own clear control). */
  function handleClearFilters(): void {
    setFilters({ ...DEFAULT_DEVICE_FILTERS });
    setCsvFilterIds(null);
    onClearMapRadiusFilter?.();
  }

  const allSelected = allVisibleIds.length > 0 && allVisibleIds.every((id) => selectedIds.includes(id));

  const rowsForTable = useMemo(() => {
    if (!idKey) return filteredRows;

    let rankedRows = filteredRows;
    if (validationModeActive) {
      const selectedSet = new Set(validationSelectedDeviceIds);
      const failedSet = new Set(Object.keys(validationFailureMap));
      const baseIndex = new Map(filteredRows.map((row, idx) => [String(row[idKey] ?? ''), idx]));
      rankedRows = [...filteredRows].sort((a, b) => {
        const aId = String(a[idKey] ?? '');
        const bId = String(b[idKey] ?? '');
        const aRank = failedSet.has(aId) ? 0 : selectedSet.has(aId) ? 1 : 2;
        const bRank = failedSet.has(bId) ? 0 : selectedSet.has(bId) ? 1 : 2;
        if (aRank !== bRank) return aRank - bRank;
        return (baseIndex.get(aId) ?? 0) - (baseIndex.get(bId) ?? 0);
      });
    }

    if (!showAdjacentPairs) return rankedRows;
    return groupPairedRows(rankedRows, idKey);
  }, [filteredRows, idKey, showAdjacentPairs, validationFailureMap, validationModeActive, validationSelectedDeviceIds]);

  const deviceDetailsDialogRow = useMemo((): DeviceMasterRow | null => {
    if (!deviceDetailsDialogId || !idKey) return null;
    const id = String(deviceDetailsDialogId).trim();
    if (!id) return null;
    const found = rowsForTable.find((r) => String(r[idKey] ?? '').trim() === id);
    return found ?? null;
  }, [deviceDetailsDialogId, rowsForTable, idKey]);

  const deviceDetailsEventScope = useMemo((): string | null => {
    if (isOperator && !isAdmin) {
      const scoped = String(operatorScopedEventId ?? '').trim();
      return scoped || null;
    }
    if (isAdmin) {
      const scoped = String(eventFilterId ?? '').trim();
      return scoped || null;
    }
    return null;
  }, [eventFilterId, isAdmin, isOperator, operatorScopedEventId]);

  useEffect(() => {
    if (!idKey || !eventFilterDeviceIds) return;
    const allowed = new Set(eventFilterDeviceIds);
    setSelectedIds((prev) => prev.filter((id) => allowed.has(id)));
  }, [eventFilterDeviceIds, idKey]);

  useEffect(() => {
    if (!isOperator || isAdmin || !idKey) return;
    const allowed = new Set(
      scopedRows
        .map((row) => row[idKey])
        .filter((id): id is string => typeof id === 'string' && id.trim().length > 0),
    );
    setSelectedIds((prev) => prev.filter((id) => allowed.has(id)));
  }, [idKey, isAdmin, isOperator, scopedRows]);

  function toggleSelectAll() {
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !allVisibleIds.includes(id)));
    } else {
      const set = new Set([...selectedIds, ...allVisibleIds]);
      setSelectedIds(Array.from(set));
    }
  }

  function toggleSelectOne(deviceId: string) {
    setSelectedIds((prev) => {
      const id = String(deviceId ?? '').trim();
      if (!id) return prev;
      const pairId = pairedFrontIdFromRear(id) ?? pairedRearIdFromFront(id);

      if (prev.includes(id)) {
        // When removing one side of a pair, remove the other side automatically to keep intent clear.
        return prev.filter((existing) => existing !== id && existing !== pairId);
      }

      return [...prev, id];
    });
  }

  function handleRowClick(deviceId?: string) {
    if (!deviceId) return;
    toggleSelectOne(deviceId);
  }

  function openDeviceDetails(deviceId: string) {
    setDeviceDetailsDialogId(deviceId);
  }

  function closeDeviceDetails() {
    setDeviceDetailsDialogId(null);
  }

  function handleOpenConfigure() {
    if (selectedIds.length === 0) return;
    setCommandDeviceIds(selectedIds);
    setShowConfigureForm(true);
  }

  /** Split selected into online/offline/busy; show offline confirm if needed, else open command form. */
  function handleSubmitCommand() {
    if (selectedIds.length === 0) return;

    const blockedIds: string[] = [];
    const offlineIds: string[] = [];
    const onlineIds: string[] = [];

    for (const id of selectedIds) {
      if (!!busyMap[id]) {
        blockedIds.push(id);
        continue;
      }

      if (!!onlineMap[id]) {
        onlineIds.push(id);
      } else {
        offlineIds.push(id);
      }
    }

    if (onlineIds.length === 0 && (offlineIds.length > 0 || blockedIds.length > 0)) {
      const parts: string[] = [];

      if (offlineIds.length > 0) {
        parts.push(summarizeDeviceIdsForToast('Offline', offlineIds));
      }

      if (blockedIds.length > 0) {
        parts.push(summarizeDeviceIdsForToast('Busy', blockedIds));
      }

      window.dispatchEvent(
        new CustomEvent('toast-warning', {
          detail: `No eligible online devices selected. ${parts.join(' | ')}`,
        }),
      );
      return;
    }

    if (offlineIds.length > 0 || blockedIds.length > 0) {
      setOfflineConfirmOnlineIds(onlineIds);
      setOfflineConfirmOfflineIds(offlineIds);
      setOfflineConfirmBlockedIds(blockedIds);
      setShowOfflineConfirm(true);
      return;
    }

    setCommandDeviceIds(onlineIds);
    setShowCommandForm(true);
  }

  function handleConfirmCommand(params: {
    operation: CommandOperation;
    deviceIds: string[];
    requestId?: string;
  }) {
    setShowCommandForm(false);
    setCommandDeviceIds([]);

    const rid = String(params.requestId || '').trim();
    if (!rid) return;

    void activateRequest(rid, params.deviceIds);
    setUrlRequestId(rid);
  }

  function handleProceedWithOnlineDevices() {
    if (offlineConfirmOnlineIds.length === 0) {
      setShowOfflineConfirm(false);
      return;
    }
    setCommandDeviceIds(offlineConfirmOnlineIds);
    setShowOfflineConfirm(false);
    setShowCommandForm(true);
  }

  function handleSearchChange(value: string) {
    setSearchQuery(value);
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(STORAGE_SEARCH_KEY, value);
    } catch {
    }
  }

  function toDeviceMappingStringRows(rows: DeviceMappingRow[]): Record<string, string>[] {
    return rows.map((row) => {
      const out: Record<string, string> = {};
      Object.entries(row).forEach(([k, v]) => {
        out[k] = v == null ? '' : String(v);
      });
      return out;
    });
  }

  /** Device ID and Role are always shown/required, regardless of whether the live
   *  table currently has any rows with those columns. */
  function withRequiredAddDeviceColumns(cols: string[]): string[] {
    const withName = cols.includes('name') ? cols : ['name', ...cols];
    if (withName.includes('deviceRole')) return withName;
    const nameIdx = withName.indexOf('name');
    return [...withName.slice(0, nameIdx + 1), 'deviceRole', ...withName.slice(nameIdx + 1)];
  }

  async function openAddDeviceDialog(): Promise<void> {
    setShowAddDeviceDialog(true);
    setAddDeviceMode('single');
    setAddDeviceValues({ name: '', deviceRole: '' });
    setBulkCsvFileName('');
    setBulkCsvRows([]);
    setBulkCsvError('');
    if (!window.eventsAPI?.getDeviceMappings) {
      setAddDeviceColumns(['name', 'deviceRole']);
      return;
    }
    setAddDeviceColumnsLoading(true);
    try {
      const res = await window.eventsAPI.getDeviceMappings();
      const raw = (res?.ok && Array.isArray(res?.data?.devices) ? res.data.devices : []) as DeviceMappingRow[];
      const cols = withRequiredAddDeviceColumns(buildDeviceMappingColumns(raw));
      setAddDeviceColumns(cols);
      setAddDeviceValues(Object.fromEntries(cols.map((col) => [col, ''])));
    } catch {
      setAddDeviceColumns(['name', 'deviceRole']);
    } finally {
      setAddDeviceColumnsLoading(false);
    }
  }

  function updateAddDeviceValue(column: string, value: string): void {
    setAddDeviceValues((prev) => ({ ...prev, [column]: value }));
  }

  async function submitAddDevice(): Promise<void> {
    const id = String(addDeviceValues.name ?? '').trim();
    if (!id) return;
    const role = String(addDeviceValues.deviceRole ?? '').trim();
    if (role !== 'front' && role !== 'rear') {
      window.dispatchEvent(new CustomEvent('toast-error', { detail: 'Select a role (front or rear)' }));
      return;
    }
    if (!window.eventsAPI?.getDeviceMappings || !window.eventsAPI?.putDeviceMappings) {
      window.dispatchEvent(new CustomEvent('toast-error', { detail: 'Device mappings API is not available' }));
      return;
    }

    setAddDeviceSaving(true);
    try {
      const res = await window.eventsAPI.getDeviceMappings();
      if (!res?.ok) throw new Error(res?.error || 'Failed to load device mappings');
      const raw = (Array.isArray(res?.data?.devices) ? res.data.devices : []) as DeviceMappingRow[];

      const existingIds = buildDeviceMappingIdSet(raw);
      const alreadyExists = Array.from(existingIds).some((existingId) => existingId.toLowerCase() === id.toLowerCase());
      if (alreadyExists) {
        window.dispatchEvent(new CustomEvent('toast-error', { detail: `${id} is already in the mappings table` }));
        return;
      }

      const newRow = normalizeDeviceMappingRow({ ...addDeviceValues, name: id });
      const putRes = await window.eventsAPI.putDeviceMappings(toDeviceMappingStringRows([...raw, newRow]));
      if (!putRes?.ok) throw new Error(putRes?.error || 'Failed to save device mappings');

      window.dispatchEvent(new CustomEvent('toast-info', { detail: `Added ${id}` }));
      setShowAddDeviceDialog(false);
      setAddDeviceValues({ name: '', deviceRole: '' });
      handleHardRefresh();
    } catch (err: any) {
      window.dispatchEvent(new CustomEvent('toast-error', { detail: err?.message || 'Failed to add device' }));
    } finally {
      setAddDeviceSaving(false);
    }
  }

  async function handleBulkCsvFile(file: File): Promise<void> {
    setBulkCsvFileName(file.name);
    setBulkCsvError('');
    setBulkCsvRows([]);
    try {
      const text = await file.text();
      const parsed = parseDeviceMappingsCsv(text);
      if (parsed.error) {
        setBulkCsvError(parsed.error);
        return;
      }
      setBulkCsvRows(parsed.rows);
    } catch (err: any) {
      setBulkCsvError(err?.message || 'Failed to read CSV file');
    }
  }

  async function submitBulkUpload(): Promise<void> {
    if (bulkCsvRows.length === 0) return;
    if (!window.eventsAPI?.getDeviceMappings || !window.eventsAPI?.putDeviceMappings) {
      window.dispatchEvent(new CustomEvent('toast-error', { detail: 'Device mappings API is not available' }));
      return;
    }

    setBulkUploading(true);
    try {
      const res = await window.eventsAPI.getDeviceMappings();
      if (!res?.ok) throw new Error(res?.error || 'Failed to load device mappings');
      const raw = (Array.isArray(res?.data?.devices) ? res.data.devices : []) as DeviceMappingRow[];

      const existingLower = new Set(Array.from(buildDeviceMappingIdSet(raw)).map((id) => id.toLowerCase()));
      const newRows: DeviceMappingRow[] = [];
      let skipped = 0;
      for (const row of bulkCsvRows) {
        const id = String(row.name ?? '').trim();
        if (existingLower.has(id.toLowerCase())) {
          skipped++;
          continue;
        }
        newRows.push(normalizeDeviceMappingRow(row));
      }

      if (newRows.length === 0) {
        window.dispatchEvent(
          new CustomEvent('toast-error', { detail: `All ${skipped} device(s) already exist in the mappings table` }),
        );
        return;
      }

      const putRes = await window.eventsAPI.putDeviceMappings(toDeviceMappingStringRows([...raw, ...newRows]));
      if (!putRes?.ok) throw new Error(putRes?.error || 'Failed to save device mappings');

      window.dispatchEvent(
        new CustomEvent('toast-info', {
          detail: `Added ${newRows.length} device${newRows.length === 1 ? '' : 's'}`
            + (skipped > 0 ? `, skipped ${skipped} already existing` : ''),
        }),
      );
      setShowAddDeviceDialog(false);
      setBulkCsvFileName('');
      setBulkCsvRows([]);
      setBulkCsvError('');
      handleHardRefresh();
    } catch (err: any) {
      window.dispatchEvent(new CustomEvent('toast-error', { detail: err?.message || 'Failed to upload devices' }));
    } finally {
      setBulkUploading(false);
    }
  }

  async function handleRunDeviceValidation(params: {
    eventId: string;
    raceId: string;
    deviceIds: string[];
  }): Promise<void> {
    const eventId = String(params.eventId ?? '').trim();
    const raceId = String(params.raceId ?? '').trim();
    const deviceIds = Array.from(
      new Set(
        (params.deviceIds ?? [])
          .map((id) => String(id ?? '').trim())
          .filter((id) => id.length > 0),
      ),
    );
    if (!eventId) {
      window.dispatchEvent(new CustomEvent('toast-error', { detail: 'Select an event first' }));
      throw new Error('Select an event first');
    }
    if (!raceId) {
      window.dispatchEvent(new CustomEvent('toast-error', { detail: 'Select a race first' }));
      throw new Error('Select a race first');
    }
    if (deviceIds.length === 0) {
      window.dispatchEvent(new CustomEvent('toast-error', { detail: 'No devices selected' }));
      throw new Error('No devices selected');
    }
    const api = window.eventsAPI as any;
    if (!api?.getRaceConfiguration || !api?.queryDeviceStatuses) {
      window.dispatchEvent(new CustomEvent('toast-error', { detail: 'Validation API is not available' }));
      throw new Error('Validation API is not available');
    }

    // Include paired front IDs so firmwareFront can be checked even when the
    // user only selected rear devices for validation.
    const statusQueryIds = Array.from(
      new Set([
        ...deviceIds,
        ...deviceIds.map((id) => pairedFrontIdFromRear(id)).filter((id): id is string => !!id),
      ]),
    );

    setValidationModeBusy(true);
    try {
      const [raceRes, statusRes] = await Promise.all([
        api.getRaceConfiguration({ eventId, raceId }),
        api.queryDeviceStatuses(statusQueryIds),
      ]);
      if (!raceRes?.ok) {
        const msg = formatApiResponseError(raceRes, 'Failed to load race configuration');
        window.dispatchEvent(new CustomEvent('toast-error', { detail: msg }));
        throw new Error(msg);
      }
      if (!statusRes?.ok) {
        const msg = formatApiResponseError(statusRes, 'Failed to load device statuses');
        window.dispatchEvent(new CustomEvent('toast-error', { detail: msg }));
        throw new Error(msg);
      }

      const cfgData = (raceRes.data ?? {}) as Record<string, unknown>;
      const cfgRow = (cfgData.configuration ?? {}) as Record<string, unknown>;
      const cfg: RaceConfigurationRequirements = {
        fw_version: typeof cfgRow.fw_version === 'string' ? cfgRow.fw_version.trim() : '',
        fw_version_front: typeof cfgRow.fw_version_front === 'string' ? cfgRow.fw_version_front.trim() : '',
        min_battery: Number.isFinite(Number(cfgRow.min_battery)) ? Number(cfgRow.min_battery) : undefined,
        max_debug_size: Number.isFinite(Number(cfgRow.max_debug_size)) ? Number(cfgRow.max_debug_size) : undefined,
        max_race_size: Number.isFinite(Number(cfgRow.max_race_size)) ? Number(cfgRow.max_race_size) : undefined,
      };

      const statusRows = Array.isArray(statusRes?.data?.statuses) ? statusRes.data.statuses : [];
      const statusById: Record<string, DeviceStatus> = {};
      for (const raw of statusRows) {
        if (!raw || typeof raw !== 'object') continue;
        const r = raw as Record<string, unknown>;
        const did = typeof r.deviceId === 'string' ? r.deviceId.trim() : '';
        if (did) statusById[did] = r as DeviceStatus;
      }

      const failures: Record<string, string[]> = {};
      for (const deviceId of deviceIds) {
        const pairedFrontId = pairedFrontIdFromRear(deviceId);
        const frontStatus = pairedFrontId ? statusById[pairedFrontId] : undefined;
        const reasons = getDeviceValidationMismatchReasons(
          cfg,
          statusById[deviceId],
          lifecycleMap[deviceId],
          undefined,
          frontStatus,
        );
        if (reasons.length > 0) failures[deviceId] = reasons;
      }

      setValidationRaceCfg(cfg);
      setValidationStatusById(statusById);
      setValidationModeActive(true);
      setValidationEventId(eventId);
      setValidationRaceId(raceId);
      setValidationSelectedDeviceIds(deviceIds);
      setValidationFailureMap(failures);
      setShowValidationReport(true);
      window.dispatchEvent(
        new CustomEvent('toast-info', {
          detail: `Validation complete: ${Object.keys(failures).length}/${deviceIds.length} failed`,
        }),
      );
    } finally {
      setValidationModeBusy(false);
    }
  }

  function exitValidationMode(): void {
    setValidationModeActive(false);
    setValidationEventId('');
    setValidationRaceId('');
    setValidationSelectedDeviceIds([]);
    setValidationFailureMap({});
    setValidationRaceCfg(null);
    setValidationStatusById({});
    setShowValidationReport(false);
  }

  const validationReportRows = useMemo(() => {
    if (!validationModeActive || !validationRaceCfg || validationSelectedDeviceIds.length === 0) return [];
    const out: RaceValidationReportRow[] = [];
    for (const id of validationSelectedDeviceIds) {
      const status = validationStatusById[id] ?? statusMap[id];
      const pairedFrontId = pairedFrontIdFromRear(id);
      const frontStatus = pairedFrontId
        ? validationStatusById[pairedFrontId] ?? statusMap[pairedFrontId]
        : undefined;
      const breakdown = getDeviceValidationBreakdown(validationRaceCfg, status, lifecycleMap[id], undefined, frontStatus);
      out.push({ deviceId: id, breakdown });
    }
    out.sort((a, b) => {
      const aFail = a.breakdown.notes ? 0 : 1;
      const bFail = b.breakdown.notes ? 0 : 1;
      if (aFail !== bFail) return aFail - bFail;
      return a.deviceId.localeCompare(b.deviceId);
    });
    return out;
  }, [
    lifecycleMap,
    statusMap,
    validationModeActive,
    validationRaceCfg,
    validationSelectedDeviceIds,
    validationStatusById,
  ]);

  return (
    <div className="h-full min-h-0 flex flex-col bg-slate-100 dark:bg-slate-800">
      {/* Page title bar */}
      <div className={`shrink-0 ${PAGE_TAB_HEADER}`}>
        <h1 className="text-lg font-semibold text-gray-900 dark:text-slate-100">Devices</h1>
      </div>

      <div className="flex-1 min-h-0 p-4">
        <div className="h-full min-h-0 flex gap-4">
          <main className="flex-1 min-w-0 min-h-0 flex flex-col gap-4">
            <div className="flex-1 min-h-0 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm p-4 flex flex-col">
              <div className="shrink-0 space-y-3">
                {mapSelectedDeviceIds && mapSelectedDeviceIds.length > 0 && (
                  <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900 dark:border-blue-700/60 dark:bg-blue-950/40 dark:text-blue-200 dark:border-blue-700/60 dark:bg-blue-950/40 dark:text-blue-200 dark:border-blue-700/60 dark:bg-blue-950/40 dark:text-blue-200">
                    Map selection filter active: {mapSelectedDeviceIds.length} device
                    {mapSelectedDeviceIds.length === 1 ? '' : 's'}.
                  </div>
                )}
                {(isAdmin || activeRequestId) && (
                  <div className={[SCOPE_PANEL_SURFACE, 'px-4 py-3'].join(' ')}>
                    <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
                      {isAdmin && (
                        <div className="min-w-0 w-full sm:flex-1 sm:max-w-md">
                          <label
                            htmlFor="device-event-filter"
                            className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400"
                          >
                            Event scope
                          </label>
                          <select
                            id="device-event-filter"
                            className={SELECT}
                            value={eventFilterId}
                            disabled={eventFilterEventsLoading || eventFilterDevicesLoading}
                            onChange={(e) => {
                              setEventFilterId(e.target.value);
                              setRaceFilterId('');
                              setEventFilterUnretrievedOnly(false);
                            }}
                          >
                            <option value="">All devices</option>
                            {eventFilterEvents.map((ev) => (
                              <option key={ev.eventId} value={ev.eventId}>
                                {ev.name ? `${ev.name} (${ev.eventId})` : ev.eventId}
                              </option>
                            ))}
                          </select>
                          {(eventFilterEventsLoading ||
                            eventFilterRacesLoading ||
                            eventFilterDevicesLoading) && (
                            <p className="mt-1.5 text-[11px] text-gray-500 dark:text-slate-400">
                              Loading events, races, or assignments…
                            </p>
                          )}
                        </div>
                      )}
                      {isAdmin && (
                        <div className="min-w-0 w-full sm:flex-1 sm:max-w-md">
                          <label
                            htmlFor="device-race-filter"
                            className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400"
                          >
                            Race scope
                          </label>
                          <div className="flex flex-wrap items-center gap-2">
                            <select
                              id="device-race-filter"
                              className={`${SELECT} min-w-0 flex-1 sm:max-w-none`}
                              value={raceFilterId}
                              disabled={
                                !eventFilterId ||
                                eventFilterRacesLoading ||
                                eventFilterDevicesLoading
                              }
                              onChange={(e) => {
                                setRaceFilterId(e.target.value);
                                setEventFilterUnretrievedOnly(false);
                              }}
                              title={
                                !eventFilterId
                                  ? 'Choose an event first to filter by race'
                                  : 'Limit devices to one race, or entire event'
                              }
                            >
                              <option value="">
                                {eventFilterId ? 'All races (entire event)' : 'Select an event first'}
                              </option>
                              {eventFilterRaces.map((r) => (
                                <option key={r.raceId} value={r.raceId}>
                                  {`${r.name ? `${r.name} (${r.raceId})` : r.raceId}${r.status ? ` — ${r.status}` : ''}`}
                                </option>
                              ))}
                            </select>
                            {eventFilterId && raceFilterId.trim() ? (
                              <>
                                <button
                                  type="button"
                                  className={`${BTN_SECONDARY} shrink-0 whitespace-nowrap`}
                                  disabled={
                                    eventFilterDevicesLoading ||
                                    validationModeBusy ||
                                    !eventFilterDeviceIds ||
                                    eventFilterDeviceIds.length === 0
                                  }
                                  onClick={() =>
                                    void handleRunDeviceValidation({
                                      eventId: eventFilterId,
                                      raceId: raceFilterId.trim(),
                                      deviceIds: eventFilterDeviceIds ?? [],
                                    })
                                  }
                                  title="Validate all devices assigned to this race against the race configuration"
                                >
                                  Run validation for this race
                                </button>
                                {!eventFilterDevicesLoading &&
                                eventFilterDeviceIds &&
                                eventFilterDeviceIds.length === 0 ? (
                                  <span className="text-[11px] text-gray-500 dark:text-slate-400">No devices on this race.</span>
                                ) : null}
                              </>
                            ) : null}
                          </div>
                        </div>
                      )}
                      {activeRequestId ? (
                        <div className="flex w-full flex-col gap-1 sm:w-auto sm:shrink-0">
                          <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400 sm:hidden">
                            Active request
                          </span>
                          <DeviceListRequestChip
                            activeRequestId={activeRequestId}
                            requestFilterIds={requestFilterIds}
                            onClear={clearActiveRequest}
                            chipClassName={REQUEST_FILTER_CHIP}
                          />
                        </div>
                      ) : null}
                    </div>
                    {isAdmin && validationModeActive && (
                      <div
                        className="mt-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950 shadow-sm dark:border-amber-700/60 dark:bg-amber-950/40 dark:text-amber-100"
                        role="status"
                      >
                        <div className="min-w-0 flex-1 space-y-1">
                          <div className="font-semibold leading-tight text-amber-900 dark:text-amber-100">
                            Event validation mode
                            <span className="ml-1 break-all font-mono text-[11px] font-normal text-amber-800/95 dark:text-amber-200/90">
                              (
                              {validationRaceId
                                ? `${validationEventId} · race ${validationRaceId}`
                                : validationEventId}
                              )
                            </span>
                          </div>
                          <p className="text-[11px] leading-snug text-amber-900/90 dark:text-amber-200/90">
                            Failed devices are pinned to top and highlighted. Hover over a device for mismatch
                            details. Update requirements on the Events page (race configuration for that race).
                          </p>
                        </div>
                        <button
                          type="button"
                          className="shrink-0 rounded-md p-1 text-amber-800 hover:bg-amber-100/90 disabled:cursor-not-allowed disabled:opacity-50 dark:text-amber-200 dark:hover:bg-amber-900/50"
                          onClick={exitValidationMode}
                          disabled={validationModeBusy}
                          aria-label="Exit validation mode"
                          title="Exit validation mode"
                        >
                          <X className="h-4 w-4" strokeWidth={2} aria-hidden />
                        </button>
                      </div>
                    )}
                    {isAdmin && eventFilterError && (
                      <p className="mt-2 text-xs text-red-600" role="alert">
                        {eventFilterError}
                      </p>
                    )}
                  </div>
                )}

                <DeviceListToolbar
                  isAdmin={isAdmin}
                  selectedIds={selectedIds}
                  onClearSelection={() => setSelectedIds([])}
                  searchQuery={searchQuery}
                  onSearchChange={handleSearchChange}
                  onOpenSelectRequest={() => setSelectRequestOpen(true)}
                  columns={columns}
                  visibleMasterColumns={visibleMasterColumns}
                  visibleExtraColumns={visibleExtraColumns}
                  onToggleMasterColumn={(col) =>
                    toggleMasterColumn(col, columns, setVisibleMasterColumns)
                  }
                  onToggleExtraColumn={(key) => toggleExtraColumn(key, setVisibleExtraColumns)}
                  columnsMenuOpen={columnsMenuOpen}
                  setColumnsMenuOpen={setColumnsMenuOpen}
                  onOpenConfigure={handleOpenConfigure}
                  onSubmitCommand={handleSubmitCommand}
                  locationFilterActive={locationFilterActive}
                  onClearLocationFilter={handleClearLocationFilter}
                  showAdjacentPairs={showAdjacentPairs}
                  onToggleAdjacentPairs={setShowAdjacentPairs}
                  activeFilterCount={activeFilterCount}
                  onClearFilters={handleClearFilters}
                  filters={filters}
                  onChangeFilters={setFilters}
                  onCsvFilterIdsChange={setCsvFilterIds}
                  lifecycleFilterEnabled={isAdmin}
                  activeRequestId={activeRequestId}
                  requestFilterIds={requestFilterIds}
                  onClearRequest={clearActiveRequest}
                  onRefresh={handleHardRefresh}
                  refreshing={hardRefreshing}
                  onOpenAddDevice={() => void openAddDeviceDialog()}
                />
              </div>

              {/* Table area: loading/error/empty or DeviceTable */}
              <div className="mt-4 flex-1 min-h-0 flex flex-col">
                {isOperator && !isAdmin && (
                  <div className="mb-3 shrink-0 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900 dark:border-blue-700/60 dark:bg-blue-950/40 dark:text-blue-100">
                    {operatorSelectedEventLabel
                      ? `Operator access: live status scoped to ${operatorSelectedEventLabel}. Select any device to add it to your event.`
                      : 'Select an event from the Events tab to enable live status and commands.'}
                  </div>
                )}
                {((isAdmin && eventFilterUnretrievedOnly && eventFilterId) ||
                  (isOperator && !isAdmin && operatorScopedUnretrievedOnly && operatorScopedEventId)) && (
                  <div className="mb-3 shrink-0 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950 dark:border-amber-700/60 dark:bg-amber-950/40 dark:text-amber-100">
                    Showing unretrieved rear devices only
                    {eventFilterDeviceIds != null
                      ? ` (${eventFilterDeviceIds.length} device${eventFilterDeviceIds.length === 1 ? '' : 's'}).`
                      : operatorScopedDeviceIds != null
                        ? ` (${operatorScopedDeviceIds.length} device${operatorScopedDeviceIds.length === 1 ? '' : 's'}).`
                        : '.'}
                  </div>
                )}

                {effectiveError && (
                  <div className="flex-1 min-h-0 flex items-center justify-center">
                    <div className="text-sm text-red-600 dark:text-red-400">{effectiveError}</div>
                  </div>
                )}

                {!effectiveError && scopedRows.length === 0 && !effectiveLoading && (
                  <div className="flex-1 min-h-0 flex items-center justify-center">
                    <div className="text-sm text-gray-500 dark:text-slate-400">
                      {'No devices found for the selected view.'}
                    </div>
                  </div>
                )}

                {!effectiveError && (scopedRows.length > 0 || effectiveLoading) && (
                  <div className="flex-1 min-h-0 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm overflow-hidden">
                    <TableLoadingOverlay
                      loading={effectiveLoading}
                      loadingPhrases={deviceListLoadingPhrases}
                      className="h-full"
                    >
                      {scopedRows.length > 0 && (
                        <DeviceTable
                          sortedRows={rowsForTable}
                          columns={columns}
                          visibleMasterColumns={visibleMasterColumns}
                          visibleExtraColumns={visibleExtraColumns}
                          statusMap={statusMap}
                          tlsMap={tlsMap}
                          selectedIds={selectedIds}
                          allSelected={allSelected}
                          latestCommandMap={displayedLatestCommandMap}
                          onlineMap={onlineMap}
                          busyMap={busyMap}
                          onToggleSelectAll={toggleSelectAll}
                          onToggleSelectOne={toggleSelectOne}
                          onSetSelectedIds={setSelectedIds}
                          onRowClick={handleRowClick}
                          onOpenDeviceDetails={openDeviceDetails}
                          requestSort={requestSort}
                          getSortIndicator={getSortIndicator}
                          onCenterMapOnLocation={onCenterMapOnLocation}
                          operatorScopedEventId={operatorScopedEventId}
                          lifecycleMap={isAdmin ? lifecycleMap : {}}
                          attributesMap={attributesMap}
                          validationFailureMap={validationModeActive ? validationFailureMap : {}}
                          userIdToDisplayName={userIdToDisplayName}
                        />
                      )}
                    </TableLoadingOverlay>
                  </div>
                )}
              </div>

              {/* Modals: offline confirm, configure, command form, select request */}
              <OfflineConfirmDialog
                open={showOfflineConfirm}
                offlineIds={offlineConfirmOfflineIds}
                blockedIds={offlineConfirmBlockedIds}
                onlineIds={offlineConfirmOnlineIds}
                onCancel={() => setShowOfflineConfirm(false)}
                onProceed={handleProceedWithOnlineDevices}
              />

              <AddDeviceDialog
                open={showAddDeviceDialog}
                mode={addDeviceMode}
                onModeChange={setAddDeviceMode}
                columns={addDeviceColumns}
                columnsLoading={addDeviceColumnsLoading}
                values={addDeviceValues}
                onValueChange={updateAddDeviceValue}
                saving={addDeviceSaving}
                onSubmit={() => void submitAddDevice()}
                bulkFileName={bulkCsvFileName}
                bulkRowCount={bulkCsvRows.length}
                bulkError={bulkCsvError}
                bulkUploading={bulkUploading}
                onBulkFileSelected={(file) => void handleBulkCsvFile(file)}
                onBulkSubmit={() => void submitBulkUpload()}
                onCancel={() => setShowAddDeviceDialog(false)}
              />

              {showConfigureForm && (
                <DeviceConfigureForm
                  selectedIds={commandDeviceIds}
                  onClose={() => {
                    setShowConfigureForm(false);
                    setCommandDeviceIds([]);
                  }}
                  onLifecycleDirty={() => {
                    bustCache(`lifecycle:${lifecycleFetchKey}`, `attrs:${lifecycleFetchKey}`);
                    setLifecycleRefreshKey((k) => k + 1);
                  }}
                  onDeviceMappingsDirty={handleHardRefresh}
                  onRunDeviceValidation={handleRunDeviceValidation}
                />
              )}

              {showCommandForm && (
                <CommandSubmitForm
                  selectedIds={commandDeviceIds}
                  operatorScopedEventId={operatorScopedEventId}
                  onClose={() => {
                    setShowCommandForm(false);
                    setCommandDeviceIds([]);
                  }}
                  onConfirm={handleConfirmCommand}
                />
              )}

              <ValidationReportDialog
                open={showValidationReport && validationModeActive}
                onClose={() => setShowValidationReport(false)}
                eventId={validationEventId || eventFilterId || ''}
                raceId={validationRaceId || ''}
                rows={validationReportRows}
                failedCount={Object.keys(validationFailureMap).length}
              />

              <SelectRequestDialog
                open={selectRequestOpen}
                operatorScopedEventId={isOperator && !isAdmin ? operatorScopedEventId : null}
                onClose={() => setSelectRequestOpen(false)}
                onSelect={(rid: string) => {
                  const id = String(rid || '').trim();
                  if (!id) return;
                  setSelectRequestOpen(false);
                  setUrlRequestId(id);
                }}
              />

              <DeviceDetailsDialog
                open={deviceDetailsDialogId != null && deviceDetailsDialogId !== ''}
                onClose={closeDeviceDetails}
                deviceId={deviceDetailsDialogId}
                row={deviceDetailsDialogRow}
                columns={columns}
                operatorScopedEventId={deviceDetailsEventScope}
                userIdToDisplayName={userIdToDisplayName}
                status={
                  deviceDetailsDialogId ? statusMap[deviceDetailsDialogId] : undefined
                }
                latestCommand={
                  deviceDetailsDialogId
                    ? displayedLatestCommandMap[deviceDetailsDialogId]
                    : undefined
                }
                onRunDeviceValidation={handleRunDeviceValidation}
              />
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}